import {
	liveSessionEnabled,
	normalizeProethicaSessionResponse,
} from "@constants/demo";
import { canEnterProethicaReview } from "@constants/demo/proethica-tests";
import { events } from "@constants/tracking";
import { isProethicsScenarioId } from "@features/proethics-simulation/data/scenario-registry";
import { hydrateProethicsReview } from "@features/proethics-simulation/helpers/reviewHydration";
import { useProethicsFlowStore } from "@features/proethics-simulation/hooks/useProethicsFlowStore";
import { flattenSessionItems } from "@helpers/session";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useQueryClient } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useState } from "react";
import { sessionItemsQueryOptions } from "../api/queries";
import useSessionStore from "./useSessionStore";

// AI-simulation items (the chat + its reflect report) are only reviewable when
// their conversation was persisted. We include them for scenarios that have a
// persisted transcript and drop them otherwise, so they never render blank.
const SIM_TASK_TYPES = new Set(["AI_SIMULATION", "AI_SIMULATION_REPORT"]);

export const useReviewSession = () => {
	const sessionItems = useSessionStore((state) => state.sessionItems);
	const sessionId = useSessionStore((state) => state.id);
	const numericId = useSessionStore((state) => state.numericId);
	const gradeItemsForReview = useSessionStore(
		(state) => state.gradeItemsForReview,
	);
	const updateInReview = useSessionStore((state) => state.updateInReview);
	const proethicaTestKind = useSessionStore((state) => state.proethicaTestKind);
	const currentExam = useDemoExamStore((state) => state.currentExam);
	const queryClient = useQueryClient();
	const [isPreparingReview, setIsPreparingReview] = useState(false);

	const getFreshReviewItems = async () => {
		const items = sessionItems ?? [];
		const hasSimulation = items.some(
			(item) => item.metadata?.taskType === "AI_SIMULATION",
		);
		const numeric = Number(numericId ?? sessionId);
		if (
			!(hasSimulation && liveSessionEnabled(currentExam?.id) && numeric > 0)
		) {
			return items;
		}
		try {
			const fresh = await queryClient.fetchQuery({
				...sessionItemsQueryOptions(numeric),
				staleTime: 0,
			});
			const normalized = normalizeProethicaSessionResponse(fresh);
			return flattenSessionItems(normalized.sessionItems);
		} catch {
			return items;
		}
	};

	const handleReviewModule = async () => {
		if (isPreparingReview) {
			return;
		}
		setIsPreparingReview(true);
		try {
			await enterReview();
		} finally {
			setIsPreparingReview(false);
		}
	};

	const enterReview = async () => {
		if (!canEnterProethicaReview(proethicaTestKind)) {
			return;
		}
		const items = await getFreshReviewItems();

		// Scenarios reviewable now: either the transcript is persisted, or the live
		// run is still in the flow store. The latter covers reviewing immediately
		// after finishing, when the async answer-event may not have echoed the
		// transcript back into `/items` yet - we must not drop those sims.
		const flow = useProethicsFlowStore.getState();
		const hasFlowRun = (scenarioId?: string) => {
			if (!isProethicsScenarioId(scenarioId)) {
				return false;
			}
			const run = flow.scenarioId === scenarioId ? flow : flow.runs[scenarioId];
			return (run?.transcript?.length ?? 0) > 0;
		};

		const scenariosWithData = new Set(
			items
				.filter(
					(item) =>
						item.metadata?.taskType === "AI_SIMULATION" &&
						((item.answer?.aiResponse?.transcript?.length ?? 0) > 0 ||
							hasFlowRun(item.metadata?.scenarioId)),
				)
				.map((item) => item.metadata?.scenarioId),
		);

		const filtered = items.filter((item) => {
			const taskType = item.metadata?.taskType ?? "";
			if (SIM_TASK_TYPES.has(taskType)) {
				return scenariosWithData.has(item.metadata?.scenarioId);
			}
			return true;
		});

		if (filtered.length === 0) {
			return;
		}

		const reviewItems = filtered as unknown as Parameters<
			typeof updateInReview
		>[1];

		trackCustomEvent({
			eventName: events.reviewAnswersClicked,
			type: "MODULE",
		});

		// Rebuild the sim runs from persisted data before entering review so the
		// simulation + reflect items render their completed, read-only state.
		hydrateProethicsReview(filtered);

		gradeItemsForReview(reviewItems);
		updateInReview(true, reviewItems);
		// Route by numericId: the review route parses `[id]` as a number and a
		// cold-resumed URL fetches items by numericId, so the DB `id` (which can
		// differ) would fetch the wrong session or redirect home.
		router.push({
			pathname: "/(protected)/session/[id]/review",
			params: { id: String(numericId || sessionId) },
		});
	};

	return { handleReviewModule, isPreparingReview };
};
